import React, { useState, useEffect } from "react";
import { X, User, Loader2, Phone, Mail } from "lucide-react";
import { IRegisteredUserWithDetails } from "../../models/epcCustomer.model";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../store";
import { updateRegisteredCustomer } from "../../store/slices/epcSlice";
import Button, { ButtonType } from "../Assets/Button";
import { UserAsyncSelect } from "../Assets/UserAsyncSelect";
import { SalesRepSelect } from "./SalesRepSelect";

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => Promise<unknown>;
  customer: IRegisteredUserWithDetails | null;
  isLoading?: boolean;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  onClose,
  onUpdated,
  customer,
  isLoading = false,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [selectedUser, setSelectedUser] = useState<
    { _id: string; username: string } | null | undefined
  >(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState<
    { _id: string; username: string } | null | undefined
  >(null);
  const [formData, setFormData] = useState({
    customerName: "",
    mobileNumber: "",
    email: "",
  });
  const [errors, setErrors] = useState<{
    customerName?: string;
    mobileNumber?: string;
    email?: string;
  }>({});

  // Populate form when customer data is available
  useEffect(() => {
    if (customer) {
      setFormData({
        customerName: customer.customerName || "",
        mobileNumber: customer.mobileNumber || "",
        email: customer.email || "",
      });
    }
  }, [customer]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        customerName: "",
        mobileNumber: "",
        email: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: {
      customerName?: string;
      mobileNumber?: string;
      email?: string;
    } = {};

    // Validate customer name
    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = "Customer name must be at least 2 characters";
    }

    // Validate mobile number
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber.replace(/\s+/g, ""))) {
      newErrors.mobileNumber = "Mobile number must be 10 digits";
    }

    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateCustomer = async () => {
    if (!validateForm()) return;
    await dispatch(
      updateRegisteredCustomer({
        customerId: customer?._id || "",
        customerName: formData.customerName || "",
        mobileNumber: formData.mobileNumber || "",
        email: formData.email || "",
        cseId: selectedUser?._id,
        salesRepId: selectedSalesRep?._id,
      }),
    );
    await onUpdated();
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    // For mobile number, only allow digits and limit to 10 characters
    if (field === "mobileNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        [field]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[150] transition-all duration-300">
        {/* Modal/Sheet Container */}
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col transform transition-all overflow-hidden border border-slate-100">
          {/* Mobile Handle Bar */}
          <div className="w-full flex justify-center pt-2.5 pb-1 sm:hidden">
            <div className="w-10 h-1 bg-slate-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 bg-white">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                Edit Customer
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Update customer details and sales assignment
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 hover:bg-slate-100 active:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1"
          >
            {/* Basic Info Fields */}
            <div className="space-y-4">
              {/* Customer Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Customer Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative group">
                  <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors w-4 h-4" />
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) =>
                      handleInputChange("customerName", e.target.value)
                    }
                    placeholder="Enter customer name"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm bg-slate-50/50 border rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-100 disabled:cursor-not-allowed ${
                      errors.customerName
                        ? "border-rose-300 bg-rose-50/30 focus:border-rose-500 focus:ring-rose-500/10"
                        : "border-slate-200"
                    }`}
                  />
                </div>
                {errors.customerName && (
                  <p className="text-rose-500 text-xs font-medium mt-1.5 flex items-center gap-1">
                    {errors.customerName}
                  </p>
                )}
              </div>

              {/* Mobile Number */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Mobile Number <span className="text-rose-500">*</span>
                </label>
                <div className="relative group">
                  <Phone className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors w-4 h-4" />
                  <input
                    type="tel"
                    value={formData.mobileNumber}
                    onChange={(e) =>
                      handleInputChange("mobileNumber", e.target.value)
                    }
                    placeholder="Enter 10-digit mobile number"
                    disabled={isLoading}
                    maxLength={10}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm bg-slate-50/50 border rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-100 disabled:cursor-not-allowed ${
                      errors.mobileNumber
                        ? "border-rose-300 bg-rose-50/30 focus:border-rose-500 focus:ring-rose-500/10"
                        : "border-slate-200"
                    }`}
                  />
                </div>
                {errors.mobileNumber && (
                  <p className="text-rose-500 text-xs font-medium mt-1.5">
                    {errors.mobileNumber}
                  </p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Email Address{" "}
                  <span className="text-slate-400 font-normal lowercase">
                    (optional)
                  </span>
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter email address"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm bg-slate-50/50 border rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-100 disabled:cursor-not-allowed ${
                      errors.email
                        ? "border-rose-300 bg-rose-50/30 focus:border-rose-500 focus:ring-rose-500/10"
                        : "border-slate-200"
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-rose-500 text-xs font-medium mt-1.5">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Assignments Section */}
            <div className="pt-2 border-t border-slate-100 space-y-4">
              <div className="w-full">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Select CSE:
                </label>
                <UserAsyncSelect
                  value={selectedUser?.username || ""}
                  onChange={(user) => {
                    setSelectedUser(user);
                  }}
                />
              </div>

              {selectedUser && (
                <div className="w-full animate-fadeIn">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Select Sales Rep
                  </label>
                  <SalesRepSelect
                    userId={selectedUser._id}
                    onChange={(salesRep) => {
                      setSelectedSalesRep(salesRep);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
              <Button
                type={ButtonType.Cancel}
                onClick={onClose}
                disabled={isLoading}
                className="w-full sm:w-auto text-xs py-2.5 px-4 rounded-xl font-medium"
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.Submit}
                onClick={updateCustomer}
                disabled={isLoading}
                className="w-full sm:w-auto text-xs py-2.5 px-5 rounded-xl font-semibold shadow-sm hover:shadow transition-all"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Updating...
                  </span>
                ) : (
                  "Update Customer"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditCustomerModal;
